import React, { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, useLocation } from "react-router-dom";
import { selectAuth } from "../features/auth/authSlice";
import { selectCollapsedSections, toggleSection } from "../features/ui/uiSlice";
import { getVisibleNavSections } from "../app/navigation";
import { navIcon, ICONS } from "../shared/icons";

/**
 * Секции навигации (AUFB-67).
 *
 * Один компонент на десктопный сайдбар и на мобильный drawer — иначе две
 * копии разъедутся при первой же правке меню.
 *
 * Про аккордеоны. У директора видно 6 секций и 22 пункта — это ~880px,
 * на ноутбуке уже скролл. Секции сворачиваются, но с защитой: секция с
 * текущим маршрутом раскрыта всегда и свернуть её нельзя, иначе можно
 * спрятать страницу, на которой стоишь. По умолчанию раскрыто всё —
 * поведение идентично прежнему, регрессии нет.
 */
export function NavSections({ collapsed = false, onNavigate }) {
  const dispatch = useDispatch();
  const location = useLocation();
  const auth = useSelector(selectAuth);
  const collapsedSections = useSelector(selectCollapsedSections);

  const sections = useMemo(() => getVisibleNavSections(auth.user), [auth.user]);

  /** Секция, которой принадлежит текущий маршрут. */
  const currentSectionKey = useMemo(() => {
    const path = location.pathname;
    let best = null;
    let bestLen = -1;
    for (const section of sections) {
      for (const item of section.items) {
        const isMatch = item.to === "/" ? path === "/" : path.startsWith(item.to);
        if (isMatch && item.to.length > bestLen) {
          best = section.key;
          bestLen = item.to.length;
        }
      }
    }
    return best;
  }, [sections, location.pathname]);

  return (
    <nav className="nav">
      {sections.map((section) => {
        const isCurrent = section.key === currentSectionKey;
        // Свёрнутый сайдбар: заголовков нет, сворачивать нечего.
        const isCollapsedSection =
          !collapsed && !isCurrent && collapsedSections.includes(section.key);

        // Секция из одного пункта не подписывается: заголовок над одним
        // элементом — чистый шум.
        const showHeading = !collapsed && section.items.length > 1;

        const classes = ["section"];
        if (isCurrent) classes.push("is-current");
        if (isCollapsedSection) classes.push("is-collapsed");

        return (
          <div key={section.key} className={classes.join(" ")}>
            {showHeading && (
              <button
                type="button"
                className={`section-head${isCurrent ? " is-locked" : ""}`}
                aria-expanded={!isCollapsedSection}
                onClick={() => {
                  if (isCurrent) return;
                  dispatch(toggleSection(section.key));
                }}
              >
                <span>{section.label}</span>
                <span className="section-chevron">{ICONS.chevron}</span>
              </button>
            )}

            {collapsed && <div className="section-rule" aria-hidden="true" />}

            {!isCollapsedSection && (
              <div className="section-items">
                {section.items.map((item) => (
                  <NavLink
                    key={item.key}
                    to={item.to}
                    end
                    title={collapsed ? item.label : undefined}
                    onClick={onNavigate}
                    className={({ isActive }) =>
                      `item${isActive ? " active" : ""}`
                    }
                  >
                    <span className="item-icon">{navIcon(item.icon)}</span>
                    {!collapsed && (
                      <>
                        <span className="item-label">{item.label}</span>
                        {item.badge ? (
                          <span className="item-count">{item.badge}</span>
                        ) : null}
                      </>
                    )}
                  </NavLink>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );
}

export default NavSections;
